// Imports
import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import { ThemeProvider } from 'styled-components/macro';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import saveValue from './save-value';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { InlineLabel, Label } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import debounce from 'lodash/debounce';
import { z } from 'zod';
import parsePersistentFormValue from './utils/parse-persistent-form-value';
import uniqueId from 'lodash/uniqueId';
import BaseFieldProps from './base-field-props';


// Define the accepted props
interface Choice
	extends Omit<
		React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
		| 'id'
		| 'className'
		| 'value'
		| 'defaultValue'
		| 'type'
		| 'checked'
		| 'defaultChecked'
		| 'prefix'
		| 'placeholder'
		| 'onChange'
		| 'autoFocus'
		| 'required'
		| 'name'
	> {
	/** The label of the choice. */
	label: React.ReactElement | string;
	
	
	/** The value of the choice. */
	value: string;
}

interface Props extends BaseFieldProps {
	/** The list of choices for the field. */
	choices: Choice[];
	
	
	/** The initial value state of the field. Should match one of the available choices. Will be ignored on subsequent renders. */
	initialValue?: string;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest value as its parameter. */
	onChange?: (newValue: string) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => string) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (value: string) => void) => void;
	
	
	/** Whether to automatically focus the first choice of this field on the initial render. Defaults to `false`. */
	autoFocus?: boolean;
	
	
	/** Whether all choices of the field should be disabled. Defaults to `false`. */
	disabled?: boolean;
	
	
	/** Whether selecting a choice is required. Defaults to `false`. */
	required?: boolean;
}


// Function component
const Radio = React.forwardRef<HTMLInputElement, Props>((props: Props, ref) => {
	// Break off the props we don't want to give to the intrinsic element
	const {
		name,
		choices,
		initialValue,
		label,
		note,
		errorMessage: errorMessageProp,
		onChange: onChangeProp,
		bindGetter,
		bindSetter,
		recoverState: recoverStateProp,
		exclude: excludeProp,
		disabled,
		autoFocus,
		required,
	} = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Retrieve values from API form's context, falling back to props
	const recoverState = recoverStateProp ?? context?.recoverState;
	const errorMessage = context?.error.field === name ? context.error.message : errorMessageProp;
	
	
	// Decide if the field is excluded
	const exclude = excludeProp ?? disabled ?? undefined;
	
	
	// Use state
	const [value, setValue] = useState<string>(initialValue !== undefined ? initialValue : '');
	
	
	// Bind getter/setter
	bindGetter && bindGetter(() => value);
	bindSetter && bindSetter(setValue);
	
	
	// Add and remove getter from connected form
	let addValueGetter: NonNullable<ContextDetails>['addValueGetter'] | null = null;
	let removeValueGetter: NonNullable<ContextDetails>['removeValueGetter'] | null = null;
	
	if (context) {
		addValueGetter = context.addValueGetter;
		removeValueGetter = context.removeValueGetter;
	}
	
	useEffect(() => {
		if (addValueGetter === null || exclude === true) {
			return;
		}
		
		addValueGetter(name, () => value);
		
		return () => {
			if (removeValueGetter === null) {
				return;
			}
			
			removeValueGetter(name);
		};
	}, [name, value, addValueGetter, removeValueGetter, exclude]);
	
	
	// Handle value changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
		setValue(event.target.value);
	};
	
	
	// Debounce save value function
	const debouncedSaveValue = useCallback(debounce(saveValue, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Save new value on updates
	useUpdateOnlyEffect(() => {
		recoverState && recoverState.enabled && debouncedSaveValue(name, value, recoverState.uniqueIdentifier);
	}, [value]);
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		onChangeProp?.(value);
		context?.onChange?.();
	}, [value]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Use ref
	const hasMounted = useRef(false);
	
	
	// Attempt to recover state on mount
	if (recoverState && recoverState.enabled && hasMounted.current === false) {
		hasMounted.current = true;
		
		window.setTimeout(() => {
			const recoveredValue = parsePersistentFormValue(recoverState.uniqueIdentifier, name, z.string());
			
			if (typeof recoveredValue !== 'undefined') {
				setValue(recoveredValue);
			}
		}, 1); // Must happen asynchronously
		
		/* Don't trigger onChange here; it will be triggered before all getters are bound, and cause issues */
	}
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('radio-field-'));
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage),
	};
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			<FieldContainer as='fieldset' aria-invalid={Boolean(errorMessage)}>
				{label && (
					<Label as='legend'>
						{label}
						{note && <small>{note}</small>}
					</Label>
				)}
				
				{choices.map(({ label, value: choiceValue, ...passThroughProps }, index) => (
					<InlineLabel key={choiceValue}>
						<input
							{...passThroughProps}
							name={name}
							type='radio'
							value={choiceValue}
							onChange={handleChange}
							checked={choiceValue === value}
							required={required}
							disabled={disabled || passThroughProps.disabled}
							ref={ref}
							autoFocus={index === 0 && autoFocus ? true : undefined}
							aria-describedby={`error-field-${uniqueID.current}`}
						/>
						{` `}
						<span>{label}</span>
					</InlineLabel>
				))}
				
				{errorMessage && <FieldErrorMessage id={`error-field-${uniqueID.current}`} message={errorMessage} />}
			</FieldContainer>
		</ThemeProvider>
	);
});


// @ts-ignore: displayName does exist
Radio.displayName = 'Radio';

export default Radio;
